import { isClient } from '@vueuse/core'
import { onBeforeUnmount, onMounted, ref } from 'vue'

export default function () {
  const isListening = ref<boolean>(false)
  const transcribedText = ref<string>('')
  const recognition = ref()
  const speechEnabled = ref<boolean>(true)
  const startTime = ref<number | null>(null) // Stores recording start time
  const timer = ref<string>('00:00:00')

  function initializeSpeechToText() {
    if (!('webkitSpeechRecognition' in window) && !isClient) {
      // eslint-disable-next-line no-alert
      alert('Speech recognition not supported')

      speechEnabled.value = false

      return
    }

    // eslint-disable-next-line new-cap
    recognition.value = new (window as any).webkitSpeechRecognition()
    recognition.value.interimResults = true
    recognition.value.lang = 'en-US'
    recognition.value.continuous = true

    if ('webkitSpeechGrammarList' in window) {
      const grammar = '#JSGF V1.0; grammar punctuation; public <punc> = . | , | ? | ! | ; | : ;'
      // eslint-disable-next-line new-cap
      const speechRecognitionList = new (window as any).webkitSpeechGrammarList()
      speechRecognitionList.addFromString(grammar, 1)
      recognition.value.grammars = speechRecognitionList
      recognition.value.maxAlternatives = 1
    }

    recognition.value.addEventListener('result', (event: any) => {
      let text = ''
      for (let i = 0; i < event.results.length; i++)
        text += event.results[i][0].transcript
      transcribedText.value = text
    })

    recognition.value.addEventListener('error', (event: any) => {
      // eslint-disable-next-line no-alert
      alert(`Speech recognition error ${event.error}`)
    })

    recognition.value.addEventListener('end', () => {
      isListening.value = false
      transcribedText.value = ''
      startTime.value = null // Reset start time on end
    })
  }
  let interval: any

  function startListening() {
    if (recognition.value && !isListening.value) {
      recognition.value.start()
      isListening.value = true
      startTime.value = Date.now() // Capture start time when recording starts
      interval = setInterval(() => {
        timer.value = getElapsedTime()
      }, 1000)
    }
  }

  function stopListening() {
    if (recognition.value && isListening.value) {
      recognition.value.stop()
      isListening.value = false
      clearInterval(interval)
      timer.value = '00:00:00'
    }
  }

  // Calculate elapsed time during recording
  function getElapsedTime() {
    if (!isListening.value || !startTime.value)
      return '00:00:00'

    const now = Date.now()
    const elapsedMs = now - startTime.value
    const hours = Math.floor(elapsedMs / (60 * 60 * 1000))
    const minutes = Math.floor((elapsedMs % (60 * 60 * 1000)) / (60 * 1000))
    const seconds = Math.floor((elapsedMs % (60 * 1000)) / 1000)

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  onMounted(() => {
    initializeSpeechToText()
  })

  onBeforeUnmount(() => {
    stopListening() // Ensure recording stops on unmount
  })

  return {
    isListening,
    transcribedText,
    startListening,
    stopListening,
    speechEnabled,
    timer,
  }
}
